<!-- 📄 frontend/src/App.vue -->
<!-- ========================================================================= -->
<!-- RAVERS NETWORK - Корневой компонент приложения Vue 3                      -->
<!-- ИСПРАВЛЕНО: Убраны повторные алерты при denied, чистая работа баннера     -->
<!-- ========================================================================= -->

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { RouterView } from 'vue-router';
import { Wrench, RefreshCw, Bell } from 'lucide-vue-next';
import { useAuthStore } from '@/stores/auth';
import {
  isPushSupported,
  getPushPermissionState,
  isPushSubscribed,
  subscribeToPush,
  registerServiceWorker
} from '@/services/push';

const authStore = useAuthStore();

// Состояние полосы уведомлений в шапке
const showPushBanner = ref(false);
const isSubscribing = ref(false);

/**
 * Повторная попытка подключения при техобслуживании
 */
async function handleRetryConnection(): Promise<void> {
  await authStore.fetchCurrentUser();
}

/**
 * 💬 [ИСПРАВЛЕНО]: Точная проверка состояния разрешений перед показом баннера.
 * Если статус 'denied' (уже заблокировано), баннер скрывается, не смущая пользователя.
 */
async function checkPushPermissionPrompt(): Promise<void> {
  registerServiceWorker().catch(() => {});

  if (!authStore.isAuthenticated) {
    showPushBanner.value = false;
    return;
  }

  const supported = isPushSupported();
  const permission = getPushPermissionState();

  // Если браузер не поддерживает пуши или права уже явно заблокированы ('denied')
  if (!supported || permission === 'unsupported' || permission === 'denied') {
    showPushBanner.value = false;
    return;
  }

  // Если права уже получены, проверяем активность подписки в бэкенде
  if (permission === 'granted') {
    const subscribed = await isPushSubscribed();
    if (!subscribed) {
      // Тихо подписываем в фоне
      await subscribeToPush();
    }
    showPushBanner.value = false;
    return;
  }

  // Показываем плашку только если состояние 'default' (пользователь еще не выбирал)
  showPushBanner.value = true;
}

/**
 * Запрос уведомлений по клику пользователя на кнопку [ВКЛЮЧИТЬ]
 */
async function handleEnablePush(): Promise<void> {
  isSubscribing.value = true;
  try {
    const res = await subscribeToPush();
    if (res.success) {
      showPushBanner.value = false;
    } else {
      // Если клиент отказал или возникла ошибка — скрываем баннер и показываем сообщение
      showPushBanner.value = false;
      alert(res.message || 'Ошибка включения уведомлений.');
    }
  } catch (err: any) {
    alert(err.message || 'Ошибка регистрации уведомлений.');
  } finally {
    isSubscribing.value = false;
  }
}

// Следим за состоянием авторизации
watch(
  () => authStore.isAuthenticated,
  (isAuth) => {
    if (isAuth) {
      checkPushPermissionPrompt();
    }
  }
);

onMounted(() => {
  registerServiceWorker().then(() => {
    if (authStore.isAuthenticated) {
      checkPushPermissionPrompt();
    }
  });
});
</script>

<template>
  <div class="w-full h-full bg-background text-slate-100 flex flex-col overflow-hidden font-sans select-none relative">

    <!-- 1. ПОЛОСА В ШАПКЕ ВО ВСЮ ШИРИНУ (ПОКАЗЫВАЕТСЯ ТОЛЬКО ЕСЛИ СОСТОЯНИЕ DEFAULT) -->
    <div
      v-if="showPushBanner"
      class="w-full bg-amber-500/20 border-b border-amber-500/40 px-3 py-2 shrink-0 z-[99999] flex items-center justify-between gap-2 text-slate-100 shadow-lg backdrop-blur-md"
    >
      <div class="flex items-center gap-2.5 min-w-0">
        <div class="w-7 h-7 rounded-lg bg-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
          <Bell class="w-4 h-4 animate-pulse" />
        </div>
        <p class="text-xs font-semibold truncate">
          Включите уведомления, чтобы не пропускать сообщения!
        </p>
      </div>
      <button
        type="button"
        class="px-3.5 py-1.5 rounded-lg bg-amber-400 text-slate-950 text-xs font-black shrink-0 hover:bg-amber-300 active:scale-95 transition-all shadow disabled:opacity-50"
        :disabled="isSubscribing"
        @click="handleEnablePush"
      >
        {{ isSubscribing ? 'Подключение...' : 'ВКЛЮЧИТЬ' }}
      </button>
    </div>

    <!-- 2. ПРЕЛОАДЕР ПЕРВОГО СТАРТА -->
    <div
      v-if="!authStore.isInitialized"
      class="w-full flex-1 flex flex-col items-center justify-center gap-4 bg-background z-50"
    >
      <div class="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin shadow-glow" />
      <div class="text-xs font-mono tracking-widest text-slate-400 uppercase">
        Ravers Network
      </div>
    </div>

    <!-- 3. ЭКРАН ПЛАНОВОГО ТЕХОБСЛУЖИВАНИЯ (MAINTENANCE MODE) -->
    <div
      v-else-if="authStore.maintenanceInfo.active"
      class="w-full flex-1 flex flex-col items-center justify-center p-6 text-center bg-background z-50 animate-fadeIn"
    >
      <div class="w-16 h-16 rounded-3xl bg-surface border border-surface-border flex items-center justify-center text-brand-yellow mb-4 shadow-lg">
        <Wrench class="w-8 h-8 animate-pulse" />
      </div>

      <h1 class="text-2xl font-extrabold text-slate-100 mb-2">Техническое обслуживание</h1>
      <p class="text-sm text-slate-400 max-w-sm mb-6 leading-relaxed">
        {{ authStore.maintenanceInfo.message || 'Обновление узлов сети. Доступ будет восстановлен в ближайшее время.' }}
      </p>

      <button
        type="button"
        class="px-5 py-2.5 rounded-xl bg-surface border border-surface-border text-xs font-bold text-slate-200 hover:text-accent hover:border-accent transition-all flex items-center gap-2 shadow"
        @click="handleRetryConnection"
      >
        <RefreshCw class="w-4 h-4" />
        <span>Повторить попытку</span>
      </button>

      <div class="mt-8 text-[11px] text-slate-600 font-mono">
        Узел Хаба: #{{ authStore.maintenanceInfo.hubId || '00001' }}
      </div>
    </div>

    <!-- 4. ОСНОВНОЙ РОУТИНГ ПРИЛОЖЕНИЯ -->
    <div v-else class="w-full flex-1 flex flex-col overflow-hidden min-h-0 relative">
      <RouterView v-slot="{ Component }">
        <transition
          name="page-fade"
          mode="out-in"
          enter-active-class="transition duration-150 ease-out"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition duration-100 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <component :is="Component" />
        </transition>
      </RouterView>
    </div>

  </div>
</template>

<style scoped>
.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 0.15s ease;
}
.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
}
</style>
